import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import "../../css/items.scss";
import { serverInstance } from "../../service/axios";
import { isNotEmpty } from "../../utils/Utils";
import "./payment.scss";

const getAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

const formatPrice = (price) => (
  new Intl.NumberFormat('mn-MN', { style: 'decimal', maximumFractionDigits: 0 }).format(price)
);

const MEMBERSHIP_COUPON_PREVIEW_MEETING_ID = "1775628421050";

const Payment = ({ data, priceData, language }) => {

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const goToClientSignIn = () => {
    navigate("/client/signin", { state: { from: location } });
  };
  const { userData } = useContext(ClientAuthContext)

  const [plan, setPlan] = useState(null);
  const [qpayInvoice, setQpayInvoice] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [gotPlan, setGotPlan] = useState(false);
  const [gettingInvoice, setGettingInvoice] = useState(false);
  const [checking, setChecking] = useState(false);
  const [paid, setPaid] = useState(false);
  const [membershipActive, setMembershipActive] = useState(false);
  const [newPaidUntil, setNewPaidUntil] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [appliedCouponPercent, setAppliedCouponPercent] = useState(0);
  const [appliedCouponAmount, setAppliedCouponAmount] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponMessageType, setCouponMessageType] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [autoAppliedCouponKey, setAutoAppliedCouponKey] = useState("");
  const normalizedCouponCode = useMemo(() => couponCode.trim().toUpperCase(), [couponCode]);
  const initialCouponCode = useMemo(() => (
    String(location.state?.couponCode || searchParams.get("coupon_code") || "").trim().toUpperCase()
  ), [location.state, searchParams]);
  const initialCouponPercent = useMemo(() => {
    const percent = Number(location.state?.couponPercent || searchParams.get("coupon_percent"));
    return Number.isFinite(percent) ? percent : 0;
  }, [location.state, searchParams]);
  const initialCouponAmount = useMemo(() => {
    const amount = Number(
      location.state?.couponDiscountAmount ||
      location.state?.couponAmount ||
      searchParams.get("coupon_discount_amount") ||
      searchParams.get("coupon_amount")
    );
    return Number.isFinite(amount) ? amount : 0;
  }, [location.state, searchParams]);
  const hasInitialCouponDiscount = !!initialCouponCode && (
    Number(initialCouponPercent) > 0 || Number(initialCouponAmount) > 0
  );
  const canUseCoupon = !!userData?.id;
  const hasUnappliedCoupon = !!normalizedCouponCode && normalizedCouponCode !== appliedCouponCode;
  const membershipPricingPreview = useMemo(() => {
    if (!plan) {
      return null;
    }

    const originalAmount = Number(plan.price);
    if (!Number.isFinite(originalAmount)) {
      return null;
    }

    const couponPercent = Number(appliedCouponPercent);
    const couponAmount = Number(appliedCouponAmount);
    const percentDiscountAmount = Number.isFinite(couponPercent) && couponPercent > 0
      ? Math.round(originalAmount * Math.min(couponPercent, 100) / 100)
      : 0;
    const discountAmount = Math.max(0, Math.min(
      originalAmount,
      Number.isFinite(couponAmount) && couponAmount > 0 ? couponAmount : percentDiscountAmount
    ));

    return {
      amount: Math.max(0, originalAmount - discountAmount),
      original_amount: originalAmount,
      currency: "MNT",
      discount_amount: discountAmount,
      coupon_code: appliedCouponCode,
      coupon_discount_amount: discountAmount,
      coupon_percent: Number.isFinite(couponPercent) ? couponPercent : 0,
    };
  }, [plan, appliedCouponAmount, appliedCouponCode, appliedCouponPercent]);
  const pricingSummary = pricingInfo || membershipPricingPreview;
  const invoiceStorageKey = useMemo(() => (
    `membership_invoice_id_${plan?.id || params.id || "unknown"}_${plan?.price ?? "unknown"}_${appliedCouponCode || "no-coupon"}`
  ), [appliedCouponCode, params.id, plan?.id, plan?.price]);
  const legacyInvoiceStorageKey = "invoice_id";

  useEffect(() => {
    if (plan && userData?.email) {
      if (!gotPlan) {
        setGotPlan(true);
      }

      let paidUntil = userData.paid_until && new Date(userData.paid_until) > new Date() ? new Date(userData.paid_until) : new Date();
      paidUntil.setFullYear(paidUntil.getFullYear() + plan.term);
      setNewPaidUntil(paidUntil.toLocaleString('mn-MN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }));
    }
  }, [gotPlan, plan, userData?.email, userData?.paid_until]);

  useEffect(() => {
    if (params.id) {
      findPlan(params.id);
      window.scrollTo(0, 0);
    }
  }, [params.id]);

  useEffect(() => {
    if (userData && userData.paid_until) {
      setMembershipActive(new Date(userData.paid_until) > new Date());
    }
  }, [userData]);

  useEffect(() => {
    if (initialCouponCode) {
      setCouponCode(initialCouponCode);
    }
  }, [initialCouponCode]);

  const getCouponText = useCallback((key, englishFallback, mongolianFallback) => (
    priceData?.[key] || data?.[key] || (language ? englishFallback : mongolianFallback)
  ), [data, language, priceData]);

  useEffect(() => {
    if (!hasInitialCouponDiscount || !plan?.id || !userData?.id || appliedCouponCode === initialCouponCode) {
      return;
    }

    setCouponCode(initialCouponCode);
    setAppliedCouponCode(initialCouponCode);
    setAppliedCouponPercent(initialCouponPercent);
    setAppliedCouponAmount(initialCouponAmount);
    setCouponMessageType("success");
    setCouponMessage(getCouponText("couponApplied", "Successfully applied code", "Купон код амжилттай"));
  }, [
    appliedCouponCode,
    getCouponText,
    hasInitialCouponDiscount,
    initialCouponAmount,
    initialCouponCode,
    initialCouponPercent,
    plan?.id,
    userData?.id,
  ]);

  const clearDisplayedInvoice = useCallback(() => {
    setQpayInvoice(null);
    setInvoiceId(null);
    setPricingInfo(null);
    setSuccessMsg("");
  }, []);

  const handleCouponChange = (e) => {
    setCouponCode(e.target.value.toUpperCase());
    setAppliedCouponCode("");
    setAppliedCouponPercent(0);
    setAppliedCouponAmount(0);
    setCouponMessage("");
    setCouponMessageType("");
    setErrorMsg("");
    clearDisplayedInvoice();
  };

  const normalizeInvoicePricing = useCallback((pricing) => {
    if (!pricing) {
      return null;
    }

    const snapshot = typeof pricing === "string" ? (() => {
      try {
        return JSON.parse(pricing);
      }
      catch {
        return null;
      }
    })() : pricing;

    if (!snapshot) {
      return null;
    }

    const amount = Number(snapshot.amount);
    const originalAmount = Number(snapshot.original_amount ?? snapshot.plan_price ?? plan?.price);
    const couponDiscountAmount = Number(
      snapshot.coupon_discount_amount ??
      snapshot.coupon_amount ??
      (snapshot.coupon_code ? snapshot.discount_amount : 0)
    );

    if (!Number.isFinite(amount)) {
      return null;
    }

    return {
      amount,
      original_amount: Number.isFinite(originalAmount) ? originalAmount : Number(plan?.price || amount),
      currency: snapshot.currency || "MNT",
      discount_amount: Number(snapshot.discount_amount ?? couponDiscountAmount ?? 0),
      coupon_code: String(snapshot.coupon_code || "").trim().toUpperCase(),
      coupon_discount_amount: Number.isFinite(couponDiscountAmount) ? couponDiscountAmount : 0,
      coupon_percent: Number(snapshot.coupon_percent ?? appliedCouponPercent ?? 0),
    };
  }, [appliedCouponPercent, plan?.price]);

  const invoiceMatchesSelection = useCallback((invoice, invoicePricing) => {
    if (!invoice || !plan) {
      return false;
    }

    const expectedAmount = Number(membershipPricingPreview?.amount ?? plan.price);
    const invoiceAmount = Number(invoicePricing?.amount ?? invoice.amount);
    const invoiceCouponCode = String(invoicePricing?.coupon_code || invoice.coupon_code || "").trim().toUpperCase();
    const invoiceCouponDiscountAmount = Number(invoicePricing?.coupon_discount_amount ?? invoicePricing?.discount_amount ?? invoice.coupon_discount_amount ?? 0);
    const planPrice = Number(plan.price);

    if (invoice.email !== userData.email) {
      return false;
    }
    if (
      !appliedCouponCode &&
      Number.isFinite(expectedAmount) &&
      Number.isFinite(invoiceAmount) &&
      invoiceAmount !== expectedAmount
    ) {
      return false;
    }
    if (appliedCouponCode) {
      if (invoiceCouponCode) {
        return invoiceCouponCode === appliedCouponCode;
      }

      return invoiceCouponDiscountAmount > 0 ||
        (Number.isFinite(invoiceAmount) && Number.isFinite(planPrice) && invoiceAmount < planPrice);
    }

    return !invoiceCouponCode;
  }, [appliedCouponCode, membershipPricingPreview?.amount, plan, userData.email]);

  const applyCouponCode = useCallback(() => {
    if (!normalizedCouponCode) {
      setAppliedCouponCode("");
      setAppliedCouponPercent(0);
      setAppliedCouponAmount(0);
      setCouponMessageType("error");
      setCouponMessage(getCouponText("invalidCouponCode", "Invalid coupon code", "Купон код буруу байна"));
      return;
    }

    const token = localStorage.getItem("msidclienttoken");
    if (!token || !userData?.id) {
      setAppliedCouponCode("");
      setAppliedCouponPercent(0);
      setAppliedCouponAmount(0);
      setCouponMessageType("error");
      setCouponMessage(getCouponText("couponLoginRequired", "Please sign in to use coupon", "Купон ашиглахын тулд нэвтэрнэ үү"));
      return;
    }

    if (!plan?.id) {
      setCouponMessageType("error");
      setCouponMessage(getCouponText("planRequiredForCoupon", "Please choose a plan before using coupon", "Купон ашиглахын тулд гишүүнчлэлийн төрөл сонгоно уу"));
      return;
    }

    setApplyingCoupon(true);
    setCouponMessage("");
    setCouponMessageType("");
    setErrorMsg("");
    clearDisplayedInvoice();

    serverInstance.post("/client/invoice/membership/coupon", {
      plan_id: plan.id,
      product: "membership",
      coupon_code: normalizedCouponCode,
    }, {
      headers: getAuthHeaders(token),
    })
      .catch((err) => {
        if (![404, 405].includes(err?.response?.status)) {
          throw err;
        }

        return serverInstance.post("/client/invoice/meeting/coupon", {
          meeting_id: MEMBERSHIP_COUPON_PREVIEW_MEETING_ID,
          plan_id: plan.id,
          product: "membership",
          coupon_code: normalizedCouponCode,
        }, {
          headers: getAuthHeaders(token),
        });
      })
      .then((res) => {
        const appliedCode = String(res?.data?.coupon_code || normalizedCouponCode).trim().toUpperCase();
        const appliedPercent = Number(res?.data?.coupon_percent ?? res?.data?.percent);
        const appliedAmount = Number(res?.data?.coupon_discount_amount ?? res?.data?.coupon_amount ?? res?.data?.discount_amount);
        setCouponCode(appliedCode);
        setAppliedCouponCode(appliedCode);
        setAppliedCouponPercent(Number.isFinite(appliedPercent) ? appliedPercent : 0);
        setAppliedCouponAmount(Number.isFinite(appliedAmount) ? appliedAmount : 0);
        setCouponMessageType("success");
        setCouponMessage(getCouponText("couponApplied", "Successfully applied code", "Купон код амжилттай"));
      })
      .catch((err) => {
        const responseData = err?.response?.data;
        const responseMessage = String(responseData?.message || (typeof responseData === "string" ? responseData : "")).toLowerCase();
        setAppliedCouponCode("");
        setAppliedCouponPercent(0);
        setAppliedCouponAmount(0);
        setCouponMessageType("error");
        if (err?.response?.status === 401) {
          setCouponMessage(getCouponText("couponLoginRequired", "Please sign in to use coupon", "Купон ашиглахын тулд нэвтэрнэ үү"));
        }
        else if (responseMessage.includes("limit")) {
          setCouponMessage(getCouponText("couponLimitReached", "Coupon code limit reached", "Купон кодын хязгаар дууссан байна"));
        }
        else {
          setCouponMessage(getCouponText("invalidCouponCode", "Invalid coupon code", "Купон код буруу байна"));
        }
      })
      .finally(() => setApplyingCoupon(false));
  }, [clearDisplayedInvoice, getCouponText, normalizedCouponCode, plan?.id, userData?.id]);

  useEffect(() => {
    if (
      !initialCouponCode ||
      !plan?.id ||
      !userData?.id ||
      applyingCoupon ||
      hasInitialCouponDiscount
    ) {
      return;
    }

    const couponKey = `${plan.id}:${initialCouponCode}`;
    if (autoAppliedCouponKey === couponKey || appliedCouponCode === initialCouponCode || normalizedCouponCode !== initialCouponCode) {
      return;
    }

    setAutoAppliedCouponKey(couponKey);
    applyCouponCode();
  }, [
    appliedCouponCode,
    applyingCoupon,
    applyCouponCode,
    autoAppliedCouponKey,
    hasInitialCouponDiscount,
    initialCouponCode,
    normalizedCouponCode,
    plan?.id,
    userData?.id,
  ]);

  const findPlan = (id) => {
    serverInstance.post("/app/item", { class: "plan", id: id }, {
      headers: {
        "Content-Type": "application/json",
      }
    })
      .then((res) => {
        setPlan(res.data.item);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setErrorMsg("Гишүүнчлэлийн төрөл олдсонгүй. Та гишүүнчлэлийн хуудаснаас дахин сонгоно уу.");
        setLoading(false);
      })
  }

  const handleInvoice = () => {
    if (!userData?.email || !plan) {
      return;
    }

    if (qpayInvoice?.qr_image) {
      return;
    }

    if (gettingInvoice || applyingCoupon) {
      return;
    }

    if (hasUnappliedCoupon) {
      setCouponMessageType("error");
      setCouponMessage(getCouponText("applyCouponFirst", "Please apply coupon before payment", "Төлбөр төлөхөөс өмнө купон кодоо хэрэглэнэ үү"));
      return;
    }

    const invoice_id = localStorage.getItem(invoiceStorageKey) || (!appliedCouponCode ? localStorage.getItem(legacyInvoiceStorageKey) : "");

    if (isNotEmpty(invoice_id)) {
      console.log("Found invoice", invoice_id)
      getExistingInvoice(invoice_id);
    }
    else {
      createNewInvoice();
    }
  }

  const createNewInvoice = () => {
    setGettingInvoice(true);
    const payload = {
      plan_id: plan.id,
      coupon_code: appliedCouponCode || undefined,
    };

    serverInstance.post("/client/invoice", payload, {
      headers: getAuthHeaders(localStorage.getItem("msidclienttoken")),
    })
      .then((res) => {
        console.log("New invoice", res.data.invoice_id);
        const invoicePricing = normalizeInvoicePricing(res.data.pricing || res.data.invoice || (res.data.amount !== undefined ? {
          amount: res.data.amount,
          original_amount: plan.price,
          currency: "MNT",
          coupon_code: appliedCouponCode,
          coupon_discount_amount: membershipPricingPreview?.coupon_discount_amount,
          coupon_percent: appliedCouponPercent,
        } : null));
        if (invoicePricing && !invoiceMatchesSelection({
          ...(res.data.invoice || {}),
          amount: invoicePricing.amount,
          email: res.data.invoice?.email || userData.email,
          coupon_code: invoicePricing.coupon_code,
        }, invoicePricing)) {
          localStorage.removeItem(invoiceStorageKey);
          setInvoiceId(null);
          setQpayInvoice(null);
          setPricingInfo(null);
          setErrorMsg(language
            ? "Invoice price does not match the selected membership plan. Please try again."
            : "Нэхэмжлэхийн үнэ сонгосон гишүүнчлэлтэй тохирохгүй байна. Дахин оролдоно уу.");
          return;
        }
        if (appliedCouponCode && !invoicePricing) {
          localStorage.setItem(invoiceStorageKey, res.data.invoice_id);
          getExistingInvoice(res.data.invoice_id, false);
          return;
        }
        setInvoiceId(res.data.invoice_id);
        setQpayInvoice(res.data.qpay_invoice);
        setPricingInfo(invoicePricing);
        setErrorMsg("");
        localStorage.setItem(invoiceStorageKey, res.data.invoice_id);
        if (!appliedCouponCode) {
          localStorage.setItem(legacyInvoiceStorageKey, res.data.invoice_id);
        }
      })
      .catch((err) => {
        console.log(err);
        const responseData = err?.response?.data;
        const responseMessage = String(responseData?.message || (typeof responseData === "string" ? responseData : "")).toLowerCase();
        if (err?.response?.status === 401) {
          setErrorMsg("Та дахин нэвтрээд оролдоно уу!");
        }
        else if (appliedCouponCode && responseMessage.includes("limit")) {
          setCouponMessageType("error");
          setCouponMessage(getCouponText("couponLimitReached", "Coupon code limit reached", "Купон кодын хязгаар дууссан байна"));
          setErrorMsg("");
        }
        else if (appliedCouponCode && responseMessage.includes("coupon")) {
          setCouponMessageType("error");
          setCouponMessage(getCouponText("invalidCouponCode", "Invalid coupon code", "Купон код буруу байна"));
          setErrorMsg("");
        }
        else {
          setErrorMsg("Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа!");
        }
      })
      .finally(() => setGettingInvoice(false));
  }

  const getExistingInvoice = (invoice_id, recreateOnMismatch = true) => {
    setGettingInvoice(true);
    serverInstance.get(`/client/invoice?invoiceId=${invoice_id}`, {
      headers: getAuthHeaders(localStorage.getItem("msidclienttoken")),
    })
      .then((res) => {
        if (res.status === 200) {
          const qpayInvoice = typeof res.data.invoice.text_value === "string"
            ? JSON.parse(res.data.invoice.text_value)
            : res.data.invoice.text_value;
          const invoicePricing = normalizeInvoicePricing(res.data.invoice.pricing_snapshot || {
            amount: res.data.invoice.amount,
            original_amount: res.data.invoice.original_amount || plan.price,
            currency: res.data.invoice.currency || "MNT",
            discount_amount: res.data.invoice.discount_amount,
            coupon_code: res.data.invoice.coupon_code,
            coupon_discount_amount: res.data.invoice.coupon_discount_amount,
            coupon_percent: res.data.invoice.coupon_percent,
          });
          if (invoiceMatchesSelection(res.data.invoice, invoicePricing)) {
            console.log("Same invoice " + res.data.invoice.email, res.data.invoice.id)
            setInvoiceId(res.data.invoice.id);
            setQpayInvoice(qpayInvoice);
            setPricingInfo(invoicePricing);
            setErrorMsg("");
            localStorage.setItem(invoiceStorageKey, res.data.invoice.id);
          }
          else {
            localStorage.removeItem(invoiceStorageKey);
            if (!appliedCouponCode) {
              localStorage.removeItem(legacyInvoiceStorageKey);
            }
            if (recreateOnMismatch) {
              createNewInvoice();
            }
            else {
              setInvoiceId(null);
              setQpayInvoice(null);
              setPricingInfo(null);
              setErrorMsg(language
                ? "Invoice price does not match the selected membership plan. Please try again."
                : "Нэхэмжлэхийн үнэ сонгосон гишүүнчлэлтэй тохирохгүй байна. Дахин оролдоно уу.");
            }
          }
        }
        else {
          setSuccessMsg("");
          setErrorMsg("Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа. Код: " + res.status);
        }
      })
      .catch((err) => {
        console.log(err);
        if (err?.response?.status === 401) {
          setErrorMsg("Та дахин нэвтрээд оролдоно уу!");
        }
        else if (err?.response?.status === 404 || err?.status === 404) {
          localStorage.removeItem(invoiceStorageKey);
          if (!appliedCouponCode) {
            localStorage.removeItem(legacyInvoiceStorageKey);
          }
          if (recreateOnMismatch) {
            createNewInvoice();
          }
          else {
            setErrorMsg("Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа! 404");
          }
        }
        else {
          setErrorMsg("Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа! " + (err?.response?.status || ""));
        }
      })
      .finally(() => setGettingInvoice(false));
  }

  const checkInvoice = () => {
    setChecking(true)
    serverInstance.post("/client/invoice/check", {
      invoice_id: invoiceId
    }, {
      headers: getAuthHeaders(localStorage.getItem("msidclienttoken")),
    })
      .then((res) => {
        if (res.data.paid === true) {
          setSuccessMsg("Төлбөр амжилттай төлөгдсөн байна");
          setErrorMsg("");
          setPaid(true);
          localStorage.removeItem(invoiceStorageKey);
          localStorage.removeItem(legacyInvoiceStorageKey);
        }
        else {
          setSuccessMsg("");
          setErrorMsg("Төлбөр төлөгдөөгүй байна!");
          setPaid(false);
        }
        setChecking(false)
      })
      .catch((err) => {
        console.log(err);
        setChecking(false);
      })
  }

  const selectedPlanPrice = pricingSummary?.amount ?? plan?.price;
  const hasCouponDiscount = Number(pricingSummary?.coupon_discount_amount || 0) > 0;

  return (
    <div className='items-container payment-container'>
      <div className="upper-section">
        <div className="upper-section-inner">
          <div className="title-container">
            <h2>{data.title}</h2>
          </div>
        </div>
      </div>
      {loading ?
        <AiOutlineLoading3Quarters size={25} className="loading-spinner" />
        :
        <div className='payment'>
          {membershipActive ?<div className="membership">
            Таны гишүүнчлэл {new Date(userData.paid_until).toLocaleString('mn-MN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })} хүртэл хүчинтэй байна.
          </div> : null}

          {plan ? <>
            <div className="info-title">{data.extend}</div>
            <div className="info">{data.type}: {plan.type === "company" ? priceData.company : priceData.person}</div>
            <div className="info">{priceData.term}: {plan.term === 100 ? priceData.lifeLong : plan.term + " " + priceData.year}</div>
            <div className="info">
              {priceData.price}: {formatPrice(selectedPlanPrice)} ₮
            </div>
            <div className="info">{data.newDate}: {newPaidUntil}</div>
          </> : null}

          {plan && ((canUseCoupon && !paid) || hasCouponDiscount) ? (
            <div className="membership-coupon pricing-summary">
              {canUseCoupon && !paid ? (
                <>
                  <label htmlFor="payment-membership-coupon">{priceData.couponCode || (language ? "Coupon code" : "Купон код")}</label>
                  <div className="coupon-input-row">
                    <input
                      id="payment-membership-coupon"
                      type="text"
                      name="coupon_code"
                      value={couponCode}
                      onChange={handleCouponChange}
                      autoCapitalize="characters"
                      disabled={applyingCoupon || !!qpayInvoice?.qr_image}
                    />
                    <button
                      type="button"
                      className="coupon-apply-button"
                      disabled={applyingCoupon || !!qpayInvoice?.qr_image || !normalizedCouponCode || normalizedCouponCode === appliedCouponCode}
                      onClick={applyCouponCode}
                    >
                      {applyingCoupon ? (priceData.loading || (language ? "Please wait..." : "Түр хүлээнэ үү...")) : (priceData.applyCoupon || (language ? "Apply" : "Хэрэглэх"))}
                    </button>
                  </div>
                  {couponMessage ? (
                    <div className={`coupon-message ${couponMessageType === "success" ? "coupon-success" : "coupon-error"}`}>
                      {couponMessage}
                    </div>
                  ) : null}
                </>
              ) : null}
              {hasCouponDiscount ? (
                <div className="coupon-pricing-summary">
                  <div className="pricing-line">
                    <span>{priceData.membershipFee || (language ? "Membership fee" : "Гишүүнчлэлийн төлбөр")}</span>
                    <strong>{formatPrice(pricingSummary.amount)} ₮</strong>
                  </div>
                  <div className="pricing-discount">
                    <span>
                      {priceData.couponDiscount || (language ? "Coupon" : "Купон")}
                      {pricingSummary.coupon_code ? ` ${pricingSummary.coupon_code}` : ""}: -{formatPrice(pricingSummary.coupon_discount_amount)} ₮
                    </span>
                  </div>
                  {Number(pricingSummary.original_amount) > Number(pricingSummary.amount) ? (
                    <div className="pricing-meta">{priceData.originalPrice || (language ? "Original price" : "Үндсэн үнэ")}: {formatPrice(pricingSummary.original_amount)} ₮</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {errorMsg ? <div className="error-msg">{errorMsg}</div> : null}
          {successMsg ? <div className="success-msg">{successMsg}</div> : null}

          {paid ? null :
            <>
              {qpayInvoice?.qr_image ?
                <img className="qr-code"
                  src={`data:image/png;base64,${qpayInvoice?.qr_image}`}
                  alt="QR Code"
                  style={{ width: '300px', height: '300px' }}
                />
                :
                userData?.first_name ?
                <div className="get-invoice">
                  <button className="get" disabled={checking || gettingInvoice || applyingCoupon} onClick={handleInvoice}>Qpay-р төлөх</button>
                </div>
                :
                <div className="get-invoice">
                  <button className="get" disabled={checking} onClick={goToClientSignIn}>Нэвтрэх</button>
                </div>
              }

              { invoiceId ? <div className="check-payment">
                <button className="check" disabled={checking} onClick={checkInvoice}>Төлбөр шалгах</button>
              </div> : null
              }

              {paid || !qpayInvoice?.qr_image ? null :
              <div className="mobile-only">
                <div className="title">Гар утасны аппаар төлөх:</div>
                <div className="payment-apps">
                  {qpayInvoice?.urls.map((item, index) => (
                    <div key={index} className="payment-app">
                      <a href={item.link} target="_blank" rel="noopener noreferrer">
                        <img className="logo" src={item.logo} alt={item.description || ""} />
                        <div className="name">{item.description}</div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>}
            </>
          }
        </div>
      }
    </div>
  );
};

export default Payment;
